import { AppNamesEnum, EnvironmentNamesEnum } from "@/enums/appSettings";
import { CompanyProfile, InputOption } from "@/types/global";

export const SpeEdLabsProfile: CompanyProfile = {
  CompanyName: "Teevra Edutech Pvt Ltd",
  DomainName: "speedlabs.in",
  SocialMedia: {
    Youtube: "https://www.youtube.com/@SpeedLabs",
    Facebook: "https://www.facebook.com/speedlabsindia",
    LinkedIn: "https://www.linkedin.com/company/speedlabs",
    Instagram: "https://www.instagram.com/speedlabs.india",
    Twitter: "https://twitter.com/SpeedLabs_India",
  },
  AppStores: {
    Android: "https://play.google.com/store/apps/details?id=in.speedlabs.science",
    iOS: "https://apps.apple.com/in/app/speedlabs/id1606108997",
  },
};

export const STEMLearnProfile: CompanyProfile = {
  CompanyName: "Teevra Edutech Pvt Ltd",
  DomainName: "stemlearn.ai",
  SocialMedia: {
    Youtube: "https://www.youtube.com/@SpeedLabs",
    Facebook: "https://www.facebook.com/stemlearnai/",
    LinkedIn: "https://www.linkedin.com/company/stemlearnai",
    Instagram: "https://www.instagram.com/stemlearn.ai",
    Twitter: "https://twitter.com/SpeedLabs_India",
  },
  AppStores: {
    Android: "https://play.google.com/store/apps/details?id=ai.stemlearn.study",
    iOS: "https://apps.apple.com/us/app/stemlearn/id6754865159",
  },
};

export const CompanyProfiles = Object.freeze({
  [AppNamesEnum.SPEEDLABS]: SpeEdLabsProfile,
  [AppNamesEnum.STEMLEARN]: STEMLearnProfile,
});

// Add local device IP address for development testing
const localhostAppName = "localhost";

export const OfflineDownloadsFolder = "offline-downloads";

export const AppDefaultHostnames = Object.freeze({
  [AppNamesEnum.SPEEDLABS]: {
    [EnvironmentNamesEnum.DEVELOPMENT]: localhostAppName,
    [EnvironmentNamesEnum.STAGING]: `staging.${SpeEdLabsProfile.DomainName}`,
    [EnvironmentNamesEnum.PRODUCTION]: `practice.${SpeEdLabsProfile.DomainName}`,
  },
  [AppNamesEnum.STEMLEARN]: {
    [EnvironmentNamesEnum.DEVELOPMENT]: localhostAppName,
    [EnvironmentNamesEnum.STAGING]: `staging.${STEMLearnProfile.DomainName}`,
    [EnvironmentNamesEnum.PRODUCTION]: `practice.${STEMLearnProfile.DomainName}`,
  },
});

export const ApiBaseUrls = Object.freeze({
  [AppNamesEnum.SPEEDLABS]: {
    [EnvironmentNamesEnum.DEVELOPMENT]: `http://${localhostAppName}:8000`,
    [EnvironmentNamesEnum.STAGING]: `https://stg-data-apis.${SpeEdLabsProfile.DomainName}`,
    [EnvironmentNamesEnum.PRODUCTION]: `https://prd-data-apis-2.${SpeEdLabsProfile.DomainName}`,
  },
  [AppNamesEnum.STEMLEARN]: {
    [EnvironmentNamesEnum.DEVELOPMENT]: `http://${localhostAppName}:8000`,
    [EnvironmentNamesEnum.STAGING]: `https://stg-data-apis.${STEMLearnProfile.DomainName}`,
    [EnvironmentNamesEnum.PRODUCTION]: `https://prd-data-apis-2.${STEMLearnProfile.DomainName}`,
  },
});

export const AzureBaseUrls = Object.freeze({
  [EnvironmentNamesEnum.DEVELOPMENT]: "https://testassignment.blob.core.windows.net",
  [EnvironmentNamesEnum.STAGING]: "https://testassignment.blob.core.windows.net",
  [EnvironmentNamesEnum.PRODUCTION]: "https://questionkscimagestorage.blob.core.windows.net",
});

export const AllCarouselItems = Object.freeze({
  [AppNamesEnum.SPEEDLABS]: [
    {
      heading: "For Students",
      text: [
        "Interactive and engaging lectures",
        "Regular practice and revision",
        "Constant support and motivation",
        "24/7 availability of mentors",
      ],
      image: "students-carousel-img.svg",
    },
    {
      heading: "For Schools",
      text: [
        "AI-powered concept delivery",
        "Customized assessments",
        "Constant tracking and feedback",
        "Comprehensive reports",
      ],
      image: "schools-carousel-img.svg",
    },
    {
      heading: "For Teachers",
      text: [
        "Powerful teaching and assessment tools",
        "In-depth student progress tracking",
        "Detailed reports, trackers and feedback",
      ],
      image: "teachers-carousel-img.svg",
    },
    {
      heading: "Adaptive Practice",
      text: [
        "Allows you to practice at your own pace",
        "Complements classroom teaching",
        "Ensures consistent improvement",
      ],
      image: "adaptive-carousel-img.svg",
    },
  ],
  [AppNamesEnum.STEMLEARN]: [
    {
      heading: "Math + Science",
      text: [
        "Build strong foundational skills",
        "Understand how the world works",
        "Solve everyday real-life problems",
        "Grow curiosity through experiments",
        "The blueprint for all technology.",
      ],
      image: "science-carousel-img.svg",
    },
    {
      heading: "Coding + Technology",
      text: [
        "Build problem-solving and reasoning skills",
        "Create useful apps and solutions",
        "Understand how computers really think",
        "Work with data to decide better",
      ],
      image: "coding-carousel-img.svg",
    },
    {
      heading: "AI + Robotics",
      text: [
        "Design machines that learn smartly",
        "Make robots help people",
        "Explore creative human-machine interfaces",
        "Work with future technology",
      ],
      image: "robotics-carousel-img.svg",
    },
    {
      heading: "And More...",
      text: [
        "Explore the cosmos with SpaceTech",
        "Innovate for a sustainable planet",
        "Learn and build with cutting-edge tech",
        "Find your unique STEM passion",
      ],
      image: "space-carousel-img.svg",
    },
  ],
});

export const MAX_FOOTER_ITEMS = 4;

// Selectable FooterColor values for a NavbarViewConfig. Every ComponentColorType generates a
// matching `bg-color-{name}-dark` class (colors.scss), which is what FooterNavbarMore paints the
// icon tile with. These are category tints, not status signals — the tile colour groups a footer
// item with its menu section, so the palette names carry no severity meaning here.
export const FOOTER_COLOR_OPTIONS: Array<InputOption> = [
  { Id: "primary", Name: "Primary" },
  { Id: "secondary", Name: "Secondary" },
  { Id: "tertiary", Name: "Tertiary" },
  { Id: "success", Name: "Success (green)" },
  { Id: "warning", Name: "Warning (amber)" },
  { Id: "danger", Name: "Danger (red)" },
  { Id: "medium", Name: "Medium (grey)" },
];
